import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Landmark, Eye, ArrowLeft, LogIn } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { FormSection, Field } from "@/components/registro/RegistroParts";
import { BrandSection, OffersEditor, NEW_OFFER } from "@/components/company/InscripcionParts";
import { ProfilePreview } from "@/components/company/ProfileView";
import { SelectField } from "@/pages/Registro";
import { useDemo } from "@/lib/demo/store";
import { useI18n } from "@/lib/i18n/i18n";
import { TITULACION_LIST } from "@/lib/demo/seed";
import { BANK } from "@/lib/demo/applications";

const SECTORS = ["Tecnología e ingeniería", "Industria y energía", "Automoción", "Consultoría", "Energía", "Consultoría tecnológica", "Retail tech"];
const ROLES = ["admin", "recruiter", "recruiter"];
const EMPTY = {
  name: "", cif: "", sector: "", web: "", address: "", contactName: "", contactEmail: "", contactPhone: "", description: "",
  logoImg: null, cover: null, brandColor: "#8B0000",
  titulaciones: [], offers: [{ ...NEW_OFFER, type: "Prácticas" }], users: ROLES.map((role) => ({ name: "", email: "", role })),
};
const DEMO = {
  name: "Orbital Labs S.L.", cif: "B98765432", sector: "Tecnología e ingeniería", web: "orbitallabs.example", address: "Calle Ejemplo 10, València",
  contactName: "Laura Pons", contactEmail: "laura@orbitallabs.example", contactPhone: "+34 600 000 010",
  description: "Empresa de demostración que desarrolla soluciones de visión artificial para la industria. Buscamos talento joven para crecer con nosotros.",
  logoImg: "/img/demo-logo.svg", cover: "/img/demo-cover.jpg", brandColor: "#0F766E",
  titulaciones: ["Ingeniería Informática", "Telecomunicaciones"],
  offers: [
    { title: "Ingeniero/a de Software", type: "Prácticas", area: "Tecnología", location: "València", titulaciones: ["Ingeniería Informática"] },
    { title: "Ingeniero/a de Datos", type: "Empleo", area: "Datos", location: "València · Híbrido", titulaciones: ["Ingeniería Informática", "Telecomunicaciones"] },
  ],
  users: [{ name: "Laura Pons", email: "laura@orbitallabs.example", role: "admin" }, { name: "Marc Ribes", email: "marc@orbitallabs.example", role: "recruiter" }, { name: "Eva Soler", email: "eva@orbitallabs.example", role: "recruiter" }],
};
const REQUIRED = ["name", "cif", "sector", "contactName", "contactEmail", "description"];
const pick = (f) => ({ ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]])), offers: f.offers.filter((o) => o.title.trim()) });

const CompanyAccess = () => {
  const { state } = useDemo();
  const { t } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const go = (e) => {
    e.preventDefault();
    const v = q.trim().toLowerCase();
    const app = v && state.companyApplications.find((a) => a.ref.toLowerCase() === v || a.contactEmail.toLowerCase() === v || a.users.some((u) => u.email && u.email.toLowerCase() === v));
    if (!app) { toast.error(t("No encontramos ninguna solicitud con esos datos")); return; }
    nav(`/empresas/inscripcion/${app.id}`);
  };
  return (
    <form onSubmit={go} className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="company-access-form">
      <p className="text-sm font-bold">{t("¿Ya has inscrito tu empresa?")}</p>
      <p className="mt-1 text-xs text-slate-500">{t("Accede con tu referencia o el email de un usuario.")}</p>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Referencia o email")} className="mt-3 h-11 rounded-xl" data-testid="company-access-input" />
      <button type="submit" className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 text-sm font-bold text-white transition-opacity hover:opacity-90" data-testid="company-access-button"><LogIn className="h-4 w-4" /> {t("Acceder")}</button>
    </form>
  );
};

export default function EmpresaInscripcion() {
  const nav = useNavigate();
  const { appId } = useParams();
  const { state, submitApplication, updateApplication } = useDemo();
  const { t } = useI18n();
  const editing = appId ? state.companyApplications.find((a) => a.id === appId) : null;
  const [form, setForm] = useState(() => (editing ? { ...EMPTY, ...editing } : EMPTY));
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  if (appId && !editing) return <Navigate to="/empresas/inscripcion" replace />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, i, patch) => setForm((f) => ({ ...f, [k]: f[k].map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const toggleTit = (x) => setForm((f) => ({ ...f, titulaciones: f.titulaciones.includes(x) ? f.titulaciones.filter((y) => y !== x) : [...f.titulaciones, x] }));

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    REQUIRED.forEach((k) => { if (!form[k].trim()) errs[k] = "Campo obligatorio"; });
    if (form.contactEmail && !/^\S+@\S+\.\S+$/.test(form.contactEmail)) errs.contactEmail = "Introduce un email válido";
    if (!form.titulaciones.length) errs.titulaciones = "Selecciona al menos una titulación";
    if (!editing && (!form.users[0].name.trim() || !form.users[0].email.trim())) errs.users = "Indica al menos el usuario administrador";
    if (!editing && !consent) errs.consent = "Debes aceptar las condiciones para completar el registro";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(t("Revisa los campos marcados")); return; }
    setSending(true);
    setTimeout(() => {
      if (editing) {
        updateApplication(editing.id, pick(form));
        toast.success(t("Perfil actualizado"));
        nav(`/empresas/inscripcion/${editing.id}`);
        return;
      }
      const app = submitApplication(pick(form));
      toast.success(t("Solicitud enviada correctamente"));
      nav(`/empresas/inscripcion/${app.id}`);
    }, 800);
  };

  const Err = ({ k }) => (errors[k] ? <p className="mt-2 text-xs font-medium text-rose-600" data-testid={`company-reg-error-${k}`}>{t(errors[k])}</p> : null);
  const PreviewBtn = ({ testId, className = "" }) => (
    <button type="button" onClick={() => setPreview(true)} className={`inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 px-5 py-2.5 text-sm font-bold transition-colors hover:bg-slate-900 hover:text-white ${className}`} data-testid={testId}>
      <Eye className="h-4 w-4" /> {t("Vista previa")}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="company-registration-page">
      <SiteHeader />
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 lg:grid-cols-[280px_1fr] lg:px-10">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          {editing && <Link to={`/empresas/inscripcion/${editing.id}`} className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-upv" data-testid="company-edit-back-link"><ArrowLeft className="h-3.5 w-3.5" /> {t("Volver a la solicitud")}</Link>}
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Inscripción de empresas")}{editing ? ` · ${editing.ref}` : ""}</p>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight" data-testid="company-reg-title">{t(editing ? "Editar perfil público" : "Inscribe tu empresa")}</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{t(editing ? "Actualiza la imagen, la descripción y las ofertas que ven los participantes." : "Solicitud online, pago por transferencia y aprobación por la UPV.")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {!editing && (
              <button type="button" onClick={() => { setForm(DEMO); setErrors({}); }} className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-upv hover:text-upv" data-testid="company-reg-autofill-button">
                <Sparkles className="h-3.5 w-3.5" /> {t("Autocompletar demo")}
              </button>
            )}
            <PreviewBtn testId="company-reg-preview-button" />
          </div>
          {!editing && <CompanyAccess />}
        </aside>
        <form onSubmit={submit} noValidate className="rounded-[28px] border border-slate-200 bg-white px-6 shadow-sm sm:px-10" data-testid="company-reg-form">
          <FormSection n={1} title={t("Datos de la empresa")}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2"><Field id="name" label="Razón social" value={form.name} onChange={set("name")} error={errors.name} /></div>
              <Field id="cif" label="CIF / NIF" value={form.cif} onChange={set("cif")} error={errors.cif} />
              <SelectField id="sector" label="Sector" value={form.sector} onChange={set("sector")} options={SECTORS} error={errors.sector} />
              <Field id="web" label="Web" optional value={form.web} onChange={set("web")} />
              <Field id="address" label="Dirección" optional value={form.address} onChange={set("address")} />
              <Field id="contactName" label="Persona de contacto" value={form.contactName} onChange={set("contactName")} error={errors.contactName} />
              <Field id="contactEmail" label="Email de contacto" type="email" value={form.contactEmail} onChange={set("contactEmail")} error={errors.contactEmail} />
              <Field id="contactPhone" label="Teléfono" optional value={form.contactPhone} onChange={set("contactPhone")} />
            </div>
          </FormSection>
          <FormSection n={2} title={t("Imagen de marca")}>
            <BrandSection form={form} set={set} />
          </FormSection>
          <FormSection n={3} title={t("Perfil de reclutamiento")}>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Descripción de la empresa")}</Label>
            <Textarea value={form.description} onChange={(e) => set("description")(e.target.value)} rows={5} className={`mt-2 rounded-xl ${errors.description ? "border-rose-400" : ""}`} data-testid="company-reg-input-description" />
            <p className="mt-1.5 text-xs text-slate-400">{t("Se muestra en «Sobre la empresa» de tu perfil público.")}</p>
            <Err k="description" />
            <Label className="mt-6 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Titulaciones buscadas")}</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {TITULACION_LIST.map((x) => (
                <button key={x} type="button" onClick={() => toggleTit(x)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${form.titulaciones.includes(x) ? "border-upv bg-upv text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`company-reg-tit-${x}`}>{t(x)}</button>
              ))}
            </div>
            <Err k="titulaciones" />
            <Label className="mt-6 block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Oportunidades de empleo")}</Label>
            <OffersEditor offers={form.offers} titulaciones={form.titulaciones} onChange={set("offers")} />
          </FormSection>
          {!editing && (
            <FormSection n={4} title={t("Usuarios de la empresa (máx. 3)")}>
              <div className="space-y-4">
                {form.users.map((u, i) => (
                  <div key={i} className="grid gap-3 rounded-2xl bg-slate-50 p-4 sm:grid-cols-[120px_1fr_1fr] sm:items-center" data-testid={`company-reg-user-${i}`}>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Usuario {n}", { n: i + 1 })}<span className="mt-1 block normal-case tracking-normal text-upv">{t(u.role === "admin" ? "Administrador" : "Reclutador")}</span></span>
                    <Input value={u.name} onChange={(e) => setList("users", i, { name: e.target.value })} placeholder={t("Nombre")} className="h-11 rounded-xl bg-white" data-testid={`company-reg-user-name-${i}`} />
                    <Input value={u.email} onChange={(e) => setList("users", i, { email: e.target.value })} placeholder={t("Email")} type="email" className="h-11 rounded-xl bg-white" data-testid={`company-reg-user-email-${i}`} />
                  </div>
                ))}
              </div>
              <Err k="users" />
            </FormSection>
          )}
          <FormSection n={editing ? 4 : 5} title={t(editing ? "Publicar cambios" : "Participación y pago")}>
            {!editing && (
              <>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 p-6" data-testid="company-reg-payment-box">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-upv-light text-upv"><Landmark className="h-5 w-5" /></span>
                  <dl className="flex-1 space-y-2 text-sm">
                    <div className="flex justify-between gap-4"><dt className="text-slate-500">{t("Cuota de participación")}</dt><dd className="text-right font-semibold">{t(BANK.amount)}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-slate-500">{t("Método de pago")}</dt><dd className="text-right font-semibold">{t("Transferencia bancaria")}</dd></div>
                    <p className="pt-2 text-xs text-slate-400">{t("Recibirás los datos bancarios y una referencia al enviar la solicitud.")}</p>
                  </dl>
                </div>
                <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 h-5 w-5" data-testid="company-reg-consent-checkbox" />
                  <span className="text-sm font-semibold text-slate-800">{t("Acepto las condiciones de participación de empresas en el Foro de Empleo UPV 2027.")}</span>
                </label>
                <Err k="consent" />
              </>
            )}
            <PreviewBtn testId="company-reg-preview-bottom-button" className={editing ? "w-full" : "mt-8 w-full"} />
            <button type="submit" disabled={sending} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display text-base font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-80" data-testid="company-reg-submit-button">
              {sending ? <><Loader2 className="h-5 w-5 animate-spin" /> {t(editing ? "Guardando…" : "Enviando…")}</> : t(editing ? "GUARDAR CAMBIOS" : "ENVIAR SOLICITUD")}
            </button>
          </FormSection>
        </form>
      </div>
      <ProfilePreview open={preview} onOpenChange={setPreview} data={{ ...pick(form), id: editing?.id || "preview", name: form.name || t("Tu empresa"), stand: editing?.stand }} />
      <PageFooter />
    </div>
  );
}
